import { useCallback, useEffect, useState } from 'react';

import { api } from '../../services/api';

const FORM_VAZIO = {
  code: '',
  discountType: 'PERCENTAGE',
  valor: '',
  maxUses: '',
  validUntil: '',
  appliesToMember: false,
};

function reais(centavos) {
  return (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function rotuloDesconto(cupom) {
  return cupom.discountType === 'PERCENTAGE'
    ? `${cupom.discountValue}%`
    : reais(cupom.discountValue);
}

function rotuloValidade(iso) {
  if (!iso) return 'sem prazo';
  return `até ${new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  })}`;
}

function situacao(cupom) {
  if (!cupom.active)
    return { label: 'Desativado', cls: 'bg-parchment text-graphite' };
  if (cupom.validUntil && new Date(cupom.validUntil) < new Date())
    return { label: 'Vencido', cls: 'bg-parchment text-graphite' };
  if (cupom.maxUses != null && cupom.usedCount >= cupom.maxUses)
    return { label: 'Esgotado', cls: 'bg-[#fbf1d9] text-[#8a5a00]' };
  return { label: 'Ativo', cls: 'bg-[#e7f4ea] text-[#166534]' };
}

// Mesma conta do backend (cupom.service.js): o valor nunca cai abaixo de 1 centavo.
function aplicarDesconto(base, tipo, valor) {
  const bruto =
    tipo === 'PERCENTAGE' ? Math.round((base * valor) / 100) : valor;
  return base - Math.min(Math.max(bruto, 0), Math.max(base - 1, 0));
}

const LABEL = 'text-[10.5px] font-black uppercase tracking-wider text-graphite';
const INPUT =
  'w-full bg-paper border border-parchment rounded-xl px-3 py-2.5 text-sm font-semibold text-ink focus:outline-none focus:border-wine';

/**
 * Cupons de um evento: lista + criação. O cupom criado aqui nasce preso ao
 * evento recebido — `eventId` (evento mensal) ou `specialEventId` (evento extra).
 * `precos` alimenta só a prévia do formulário: [{ label, centavos, soMembro }].
 * `empilhado` põe o formulário abaixo da lista (para caber dentro de um modal).
 */
export default function CuponsEvento({
  eventId,
  specialEventId,
  precos = [],
  empilhado = false,
}) {
  const ehMensal = Boolean(eventId);
  const [cupons, setCupons] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroLista, setErroLista] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [erroForm, setErroForm] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get('/admin/cupons', {
        params: eventId ? { eventId } : { specialEventId },
      });
      setCupons(data);
      setErroLista(null);
    } catch (err) {
      setErroLista(
        err.response?.data?.erro ?? 'Não foi possível carregar os cupons.',
      );
    } finally {
      setCarregando(false);
    }
  }, [eventId, specialEventId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const campo = (nome) => (e) =>
    setForm((f) => ({
      ...f,
      [nome]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  // O campo de valor é em reais para desconto fixo; a API recebe centavos.
  const valorNumero = Number(String(form.valor).replace(',', '.')) || 0;
  const valorApi =
    form.discountType === 'PERCENTAGE'
      ? Math.round(valorNumero)
      : Math.round(valorNumero * 100);

  async function criar(e) {
    e.preventDefault();
    setErroForm(null);
    setSalvando(true);
    try {
      const { data } = await api.post('/admin/cupons', {
        code: form.code,
        discountType: form.discountType,
        discountValue: valorApi,
        maxUses: form.maxUses === '' ? null : Number(form.maxUses),
        // Fim do dia escolhido, em UTC — mesma convenção de datas do projeto.
        validUntil: form.validUntil ? `${form.validUntil}T23:59:59.000Z` : null,
        appliesToMember: form.appliesToMember,
        ...(eventId ? { eventId } : { specialEventId }),
      });
      setCupons((lista) => [data, ...lista]);
      setForm(FORM_VAZIO);
    } catch (err) {
      setErroForm(
        err.response?.data?.erro ?? 'Não foi possível criar o cupom.',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function alternarAtivo(cupom) {
    try {
      const { data } = await api.patch(`/admin/cupons/${cupom.id}`, {
        active: !cupom.active,
      });
      setCupons((lista) =>
        lista.map((c) => (c.id === cupom.id ? { ...c, ...data } : c)),
      );
    } catch (err) {
      setErroLista(
        err.response?.data?.erro ?? 'Não foi possível atualizar o cupom.',
      );
    }
  }

  const ativos = cupons.filter((c) => situacao(c).label === 'Ativo').length;
  const usos = cupons.reduce((acc, c) => acc + c.usedCount, 0);
  const descontos = cupons.reduce(
    (acc, c) => acc + (c.totalDiscountInCents ?? 0),
    0,
  );

  const precosPrevia = precos.filter(
    (p) => Number.isFinite(p.centavos) && (!p.soMembro || form.appliesToMember),
  );

  return (
    <div
      className={`grid gap-5 items-start ${empilhado ? '' : 'lg:grid-cols-[minmax(0,1fr)_340px]'}`}
    >
      {/* ── Lista ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-parchment p-5 sm:p-6 min-w-0">
        <div className="text-[11px] font-black uppercase tracking-wider text-graphite mb-4">
          Cupons deste evento
        </div>

        {erroLista && (
          <div className="text-sm font-bold text-red-700 mb-4" role="alert">
            {erroLista}
          </div>
        )}

        {carregando ? (
          <div className="text-sm text-graphite py-8 text-center">
            Carregando…
          </div>
        ) : cupons.length === 0 ? (
          <div className="text-sm text-graphite py-8 text-center">
            Este evento ainda não tem cupons. Crie o primeiro no formulário ao
            lado.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-8 gap-y-2 pb-4 mb-1 border-b border-parchment">
              <div>
                <div className="font-gilroy font-extrabold text-[22px] leading-tight tabular-nums text-ink">
                  {ativos}
                </div>
                <div className="text-xs text-graphite">
                  {ativos === 1 ? 'ativo' : 'ativos'}
                </div>
              </div>
              <div>
                <div className="font-gilroy font-extrabold text-[22px] leading-tight tabular-nums text-ink">
                  {usos}
                </div>
                <div className="text-xs text-graphite">usos pagos</div>
              </div>
              <div>
                <div className="font-gilroy font-extrabold text-[22px] leading-tight tabular-nums text-ink">
                  {reais(descontos)}
                </div>
                <div className="text-xs text-graphite">em descontos pagos</div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-[13px] tabular-nums">
                <thead>
                  <tr className="text-left text-[10.5px] font-black uppercase tracking-wider text-graphite">
                    <th className="px-2.5 pt-3 pb-2">Código</th>
                    <th className="px-2.5 pt-3 pb-2">Desconto</th>
                    <th className="px-2.5 pt-3 pb-2">Usos</th>
                    <th className="px-2.5 pt-3 pb-2">Validade</th>
                    <th className="px-2.5 pt-3 pb-2">Situação</th>
                    <th className="px-2.5 pt-3 pb-2" />
                  </tr>
                </thead>
                <tbody>
                  {cupons.map((c) => {
                    const sit = situacao(c);
                    const pct =
                      c.maxUses != null
                        ? Math.min((c.usedCount / c.maxUses) * 100, 100)
                        : null;
                    return (
                      <tr
                        key={c.id}
                        className={`border-t border-parchment ${c.active ? 'text-ink' : 'text-graphite'}`}
                      >
                        <td className="px-2.5 py-3 font-extrabold tracking-wide">
                          {c.code}
                          {ehMensal && c.appliesToMember && (
                            <div className="text-[11px] font-semibold text-graphite tracking-normal">
                              vale para Lily Member
                            </div>
                          )}
                        </td>
                        <td className="px-2.5 py-3">{rotuloDesconto(c)}</td>
                        <td className="px-2.5 py-3">
                          <div className="flex flex-col gap-1.5 min-w-[110px]">
                            <span>
                              {c.maxUses != null
                                ? `${c.usedCount} de ${c.maxUses}`
                                : `${c.usedCount} · sem limite`}
                            </span>
                            {c.pendingCount > 0 && (
                              <span className="text-[11px] text-graphite">
                                +{c.pendingCount} aguardando pagamento
                              </span>
                            )}
                            {pct != null && (
                              <div className="h-1.5 rounded-full bg-parchment overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-h-rosa-fill"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2.5 py-3 whitespace-nowrap">
                          {rotuloValidade(c.validUntil)}
                        </td>
                        <td className="px-2.5 py-3">
                          <span
                            className={`inline-block text-[11px] font-extrabold px-2.5 py-1 rounded-full whitespace-nowrap ${sit.cls}`}
                          >
                            {sit.label}
                          </span>
                        </td>
                        <td className="px-2.5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => alternarAtivo(c)}
                            className="text-xs font-extrabold text-wine hover:underline px-1 py-1.5"
                          >
                            {c.active ? 'Desativar' : 'Reativar'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Novo cupom ──────────────────────────────────────────── */}
      <form
        onSubmit={criar}
        className="bg-white rounded-2xl border border-parchment p-5 sm:p-6 flex flex-col gap-3.5 min-w-0"
      >
        <div className="text-[11px] font-black uppercase tracking-wider text-graphite">
          Novo cupom
        </div>

        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Código</span>
          <input
            type="text"
            value={form.code}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
              }))
            }
            placeholder="OUTUBRO15"
            maxLength={20}
            required
            autoComplete="off"
            className={`${INPUT} font-extrabold tracking-wide`}
          />
        </label>

        <fieldset className="flex flex-col gap-1.5">
          <legend className={`${LABEL} mb-1.5`}>Tipo de desconto</legend>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { id: 'PERCENTAGE', label: 'Percentual' },
              { id: 'FIXED_AMOUNT', label: 'Valor em R$' },
            ].map((t) => (
              <label
                key={t.id}
                className={`text-center text-[13px] font-extrabold py-2.5 rounded-xl border cursor-pointer has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-wine ${
                  form.discountType === t.id
                    ? 'bg-ink border-ink text-white'
                    : 'bg-paper border-parchment text-graphite'
                }`}
              >
                <input
                  type="radio"
                  name="discountType"
                  value={t.id}
                  checked={form.discountType === t.id}
                  onChange={campo('discountType')}
                  className="sr-only"
                />
                {t.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5 min-w-0">
            <span className={LABEL}>
              {form.discountType === 'PERCENTAGE'
                ? 'Desconto (%)'
                : 'Desconto (R$)'}
            </span>
            <input
              type="number"
              value={form.valor}
              onChange={campo('valor')}
              min={form.discountType === 'PERCENTAGE' ? 1 : 0.01}
              max={form.discountType === 'PERCENTAGE' ? 100 : undefined}
              step={form.discountType === 'PERCENTAGE' ? 1 : 0.01}
              required
              className={INPUT}
            />
          </label>
          <label className="flex flex-col gap-1.5 min-w-0">
            <span className={LABEL}>Limite de usos</span>
            <input
              type="number"
              value={form.maxUses}
              onChange={campo('maxUses')}
              min={1}
              step={1}
              placeholder="Sem limite"
              className={INPUT}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Válido até</span>
          <input
            type="date"
            value={form.validUntil}
            onChange={campo('validUntil')}
            className={INPUT}
          />
          <span className="text-[11.5px] text-graphite">
            Em branco, o cupom não vence.
          </span>
        </label>

        {ehMensal && (
          <div className="flex flex-col gap-2">
            <span className={LABEL}>Vale para</span>
            <label className="flex items-center gap-2 text-[13px] font-semibold text-graphite">
              <input type="checkbox" checked disabled className="w-4 h-4" />
              Ingresso Avulso
            </label>
            <label className="flex items-center gap-2 text-[13px] font-semibold text-ink cursor-pointer">
              <input
                type="checkbox"
                checked={form.appliesToMember}
                onChange={campo('appliesToMember')}
                className="w-4 h-4 accent-[#ca446c]"
              />
              Lily Member também
            </label>
          </div>
        )}

        {precosPrevia.length > 0 && valorApi > 0 && (
          <div className="bg-h-rosa-wash rounded-xl px-3.5 py-3 flex flex-col gap-1 text-[13px] tabular-nums">
            {precosPrevia.map((p) => (
              <div key={p.label} className="flex justify-between gap-3">
                <span>{p.label}</span>
                <span>
                  <s className="text-graphite">{reais(p.centavos)}</s>{' '}
                  <b className="font-extrabold">
                    {reais(
                      aplicarDesconto(p.centavos, form.discountType, valorApi),
                    )}
                  </b>
                </span>
              </div>
            ))}
          </div>
        )}

        {erroForm && (
          <div className="text-[13px] font-bold text-red-700" role="alert">
            {erroForm}
          </div>
        )}

        <button
          type="submit"
          disabled={salvando}
          className="bg-h-rosa-fill text-white text-[13px] font-black uppercase tracking-wider rounded-full py-3.5 hover:brightness-95 disabled:opacity-60"
        >
          {salvando ? 'Criando…' : 'Criar cupom'}
        </button>
      </form>
    </div>
  );
}
